import deepmerge from 'deepmerge';
import { StorageProvider } from './storage-provider';

export type State = {
    sessionId?: string;
    test?: {
        testId?: string;
        variantId?: string;
        testIdVariantId?: string;
        testName?: string;
        variantName?: string;
    }
    page?: {
        url?: {
            raw?: string;
            base?: string;
            queryParams?: string;
        }
        queryParams?: Record<string, string>;
    }
    referrer?: string;
    affiliate?: {
        id?: string | null;
        campaignId?: string | null;
    }
    platform?: {
        id?: string;
        funnelData?: {
            brand?: string;
            productLine?: string;
            funnelName?: string;
            stepName?: string;
            type?: string;
        }
    }
    device?: {
        name?: string;
        manufacturer?: string;
        type?: string;
        resolution?: {
            width?: number;
            height?: number;
        }
        viewport?: {
            width?: number;
            height?: number;
        }
        encoding?: string;
        language?: string;
        colors?: string;
    }
    browser?: {
        manufacturer?: string;
        name?: string;
        majorVersion?: string;
        version?: string;
        window?: {
            width?: number;
            height?: number;
        }
    }
    os?: {
        majorVersion?: string;
        name?: string;
        version?: string;
    }
    scrollPosition?: string;
    ipAddress?: string;
    customer?: {
        id?: number;
        email?: string;
        associatedAccountEmail?: string;
        firstName?: string;
        lastName?: string;
        phone?: string;
        billing?: {
            phone?: string;
            address?: string;
            city?: string;
            country?: string;
            state?: string;
            zip?: string;
        }
        shipping?: {
            phone?: string;
            address?: string;
            city?: string;
            country?: string;
            state?: string;
            zip?: string;
        }
    }
    order?: {
        id?: number;
        currency?: string;
        channel_id?: string;
        subtotal?: string;
        grandTotal?: string;
        shipping?: string;
        tax?: string;
        promoCode?: string;
        gpmTotal?: string;
        items: {
            id?: string;
            name?: string;
            selected_options?: string;
            brand?: string;
            category?: string;
            price?: string;
            retail?: string;
            discount?: string;
            gpm?: string;
            quantity?: number;
        }[]
    }
    timestamp?: string;
    event?: string;
};

const IGNORED_PARAMS = [
    'view',
    'domainoverride',
    'token',
    'redirect',
    'builderContext',
    'cartId',
    'localdev',
    'from'
];

export const cleanQueryParams = (init?: string[][] | Record<string, string> | string | URLSearchParams) => {
    if(!init) return new URLSearchParams();

    const params = new URLSearchParams(init);
    const paramsArray = Array.from(params);
    const cleanedParams = paramsArray.filter(([key]) => !key?.startsWith('sort.') && !key?.startsWith('filter.') && !IGNORED_PARAMS.includes(key ?? ''))
    return new URLSearchParams(cleanedParams);
}

export class CustomAnalytics {
    private debug: boolean;
    private customAnalyticsUrl?: string;
    private defaultHeaders: HeadersInit;
    static storageKey = "customAnalytics";

    constructor(customAnalyticsUrl?: string, debug?: boolean) {
        this.debug = debug ?? false;
        this.customAnalyticsUrl = customAnalyticsUrl;
        this.defaultHeaders = {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        };
    }

    hasAnalyticsContext(queryParams?: Record<string, string>) {
        return Object.entries(queryParams ?? {}).some(([k, _v]) => k.toLowerCase().startsWith('utm') || ['afid', 'aff_sub', 'trv', 'source'].includes(k.toLowerCase()));
    }

    async pageEnter(payload: State) {
        await this.sendEvent('pageEnter', payload, this.hasAnalyticsContext(payload.page?.queryParams));
        return payload;
    }

    async pageLeave(payload: State) {
        await this.sendEvent('pageLeave', payload);
        return payload;
    }

    async purchase(payload: State) {
        await this.sendEvent('purchase', payload);
        return payload;
    }

    async sendEvent(event: string, payload: State, hasNewAnalyticsContext?: boolean) {
        const state = CustomAnalytics.getState();

        const existingQueryParams = state.page?.queryParams;
        const hasExistingAnalyticsContext = this.hasAnalyticsContext(existingQueryParams);
        let pageData = {};
        if (hasNewAnalyticsContext && !hasExistingAnalyticsContext) {
            pageData = {
                ...(payload.page?.url?.raw ? { url: payload.page.url } : {}),
                ...(payload.page?.queryParams ? { queryParams: payload.page.queryParams } : {}),
            }
        } else {
            const newUrlData = payload.page?.url;
            if (newUrlData?.raw) {
                const url = new URL(newUrlData.raw);
                const params = new URLSearchParams({
                    ...url.searchParams,
                    ...existingQueryParams ?? {}
                });
                newUrlData.raw = `${url.origin}${url.pathname}?${params.toString()}`;
                newUrlData.queryParams = `?${params.toString()}`;
                pageData = {
                    url: newUrlData,
                    queryParams: existingQueryParams ?? {}
                }
            }
        }
        const newState = deepmerge<State>(state, {
            ...(payload.sessionId ? { sessionId: payload.sessionId } : {}),
            ...(payload.test?.testId ? { test: payload.test } : {}),
            page: pageData,
            ...(payload.referrer ? { referrer: payload.referrer } : {}),
            ...(Object.keys(payload.affiliate ?? {}).length ? { affiliate: payload.affiliate } : {}),
            ...(payload.platform?.id ? { platform: payload.platform } : {}),
            ...(payload.device?.name ? { device: payload.device } : {}),
            ...(payload.browser?.name ? { browser: payload.browser } : {}),
            ...(payload.os?.name ? { os: payload.os } : {}),
            ...(payload.scrollPosition ? { scrollPosition: payload.scrollPosition.toString() } : {}),
            ...(payload.ipAddress ? { ipAddress: payload.ipAddress } : {}),
            ...(Object.keys(payload.customer ?? {}).length ? { customer: payload.customer } : {}),
            timestamp: new Date().toISOString(),
            event,
        });
        // not merging the order data
        if (payload.order?.id) {
            newState.order = payload.order;
        }

        StorageProvider.setItem(CustomAnalytics.storageKey, newState);
        if (this.debug){
            console.debug(`CustomAnalytics event ${event}`, JSON.stringify(newState, undefined, 2));
        } else {
            console.debug(`CustomAnalytics event ${event}`);
        }

        if (this.customAnalyticsUrl) {
            try {
                await fetch(this.customAnalyticsUrl, {
                    method: 'POST',
                    headers: this.defaultHeaders,
                    body: JSON.stringify(newState),
                    cache: 'no-cache',
                    mode: 'no-cors',
                    keepalive: true,
                });
                console.debug(`CustomAnalytics event ${event} sent`);
            } catch (error) {
                console.error(error);
            }
        } else {
            console.warn('Custom Analytics is missing configuration: CUSTOM_ANALYTICS_URL');
        }

        return newState;
    }

    push(payload: State) {
        const state = CustomAnalytics.getState();
        const newState = deepmerge<State>(state, payload);
        StorageProvider.setItem(CustomAnalytics.storageKey, newState);
        if (this.debug)
            console.debug(`CustomAnalytics push`, JSON.stringify(newState, undefined, 2));
        return newState;
    }

    static getState() {
        const storedState = StorageProvider.getItem<State>(CustomAnalytics.storageKey);
        return storedState ?? CustomAnalytics.initialState;
    }

    static initialState: State = {};
}
